/**
 * React Query hook for fetching notification settings
 *
 * Fetches notification settings (defaults and overrides) for the active entity.
 * Settings are cached and automatically refetched when the entity changes.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { fetchNotificationSettings } from '../../api';
import type { NotificationSettingsResponse } from '../../types';
import { extractPlatformUser } from '@/utils/auth0UserUtils';
import { normalizeNotificationSettings, type StoredNotificationSettings } from '../utils';

/**
 * Query key factory for notification settings
 * Scoped by user UUID to ensure proper cache isolation
 */
export const notificationSettingsKeys = {
  all: ['notificationSettings'] as const,
  settings: (userUuid: string) => ['notificationSettings', userUuid] as const,
};

/**
 * Default notification settings used when the user hasn't configured settings yet.
 */
const DEFAULT_SETTINGS: NotificationSettingsResponse = {
  defaults: {
    informational: { inApp: true, email: false },
    actionable: { inApp: true, email: true },
    mentions: { inApp: true, email: true },
  },
  overrides: [],
};

/**
 * Enforces that actionable in-app notifications are always enabled, normalizes
 * informational digest settings, and backfills `mentions` for legacy payloads.
 *
 * Runs as `select`, not inside `queryFn`, so it also applies to cache entries
 * restored from sessionStorage that were written before a field existed.
 */
function enforceActionableEnabled(
  settings: NotificationSettingsResponse
): NotificationSettingsResponse {
  const normalizeConfig = (config: StoredNotificationSettings | undefined) =>
    normalizeNotificationSettings({
      ...config,
      actionable: {
        ...config?.actionable,
        inApp: true,
      },
    });

  return {
    defaults: normalizeConfig(settings.defaults),
    overrides: (settings.overrides ?? []).map((override) => ({
      ...override,
      settings: normalizeConfig(override.settings),
    })),
  };
}

/**
 * Hook to fetch notification settings for the current user
 * Returns defaults and entity-specific overrides from the API
 *
 * If the user has no settings yet (404 NOT_FOUND), returns default settings.
 * This allows the UI to work before the user's first PUT request.
 *
 * @returns React Query result with settings data, loading state, and error
 */
export function useNotificationSettingsQuery() {
  const { user, token } = useAuth();
  const userUuid = extractPlatformUser(user)?.uuid ?? 'default';

  return useQuery<NotificationSettingsResponse, Error>({
    queryKey: notificationSettingsKeys.settings(userUuid),
    queryFn: async () => {
      try {
        // Non-null assertion is safe because enabled guard ensures token exists
        return await fetchNotificationSettings(token!, userUuid);
      } catch (error) {
        // If settings don't exist yet (404), return default "ON" settings
        // The first PUT request will create the settings on the backend
        if (error instanceof Error && error.message.includes('404')) {
          return DEFAULT_SETTINGS;
        }
        // Re-throw other errors (network issues, auth failures, etc.)
        throw error;
      }
    },
    select: enforceActionableEnabled,
    enabled: !!userUuid && !!token && userUuid !== 'default',
    staleTime: 60 * 1000, // 1 minute - settings don't change frequently
    retry: (failureCount, error) => {
      // Don't retry 404 errors - they're expected for first-time users
      if (error instanceof Error && error.message.includes('404')) {
        return false;
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
}
